import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';

import { getCountry } from '@domains/user/store/slices/user-slice/user-slice';
import {
  getSelectedStatus,
  getSelectedProcess,
  getSelectedProcessStatus,
  getSortByOldest,
  getStartDate,
  getEndDate,
  getSelectedAlias,
} from '../store/slices/domainOrchestratorPricingCommittee.slice';
import { useGetOrchestratorsQuery } from '../store/services/getOrchestrators';

export const useOrchestratorList = () => {
  const country = useSelector(getCountry);
  const status = useSelector(getSelectedStatus);
  const selectedProcess = useSelector(getSelectedProcess);
  const selectedProcessStatus = useSelector(getSelectedProcessStatus);
  const sortByOldest = useSelector(getSortByOldest);
  const startDate = useSelector(getStartDate);
  const endDate = useSelector(getEndDate);
  const workflowAlias = useSelector(getSelectedAlias);

  const queryArg = country
    ? {
        country,
        status,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        workflow_alias: workflowAlias || undefined,
      }
    : skipToken;
  const orchestrators = useGetOrchestratorsQuery(queryArg as any);
  const { data } = orchestrators;

  const filtered = useMemo(() => {
    if (!data) return data;

    return data.filter(o => {
      const stepByName = selectedProcess
        ? o.workflow_steps.find(ws => ws.name === selectedProcess)
        : undefined;

      if (selectedProcess && selectedProcessStatus) {
        return stepByName?.status === selectedProcessStatus;
      }

      if (selectedProcess) {
        return !!stepByName;
      }

      if (selectedProcessStatus) {
        return o.workflow_steps.some(ws => ws.status === selectedProcessStatus);
      }

      return true;
    });
  }, [data, selectedProcess, selectedProcessStatus]);

  const sorted = useMemo(() => {
    if (!filtered) return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => {
      const dateA = new Date(a.start_at).getTime();
      const dateB = new Date(b.start_at).getTime();
      return sortByOldest ? dateA - dateB : dateB - dateA;
    });
    return arr;
  }, [filtered, sortByOldest]);

  return { orchestrators: { ...orchestrators, data: sorted } };
};
